/**
 * Workspace telemetry call-sites.
 *
 * The sink is deliberately a `console.debug` no-op in dev and a no-op in
 * prod until a real analytics vendor/endpoint is chosen at the platform
 * level. Wiring a real destination is a one-line change here — every
 * call-site in the codebase stays untouched.
 */

export type AiWorkspaceEventName =
  | 'WorkspaceOpened'
  | 'WorkspaceModeChanged'
  | 'SuggestionClicked'
  | 'PromptSelected'
  | 'ConversationPinned'
  | 'ConversationRenamed'
  | 'ContextBadgeClicked';

export interface AiWorkspaceEventPayload {
  [key: string]: unknown;
}

/**
 * Replace the no-op sink with an actual SDK call once a vendor is chosen:
 *   analytics.track(name, payload);  // Segment/Mixpanel/PostHog/…
 *   fetch('/api/telemetry', { method: 'POST', body: JSON.stringify(...) });
 */
const sink = (name: AiWorkspaceEventName, payload?: AiWorkspaceEventPayload): void => {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug(`[AI Workspace] ${name}`, payload ?? {});
  }
};

export const trackWorkspaceEvent = (
  name: AiWorkspaceEventName,
  payload?: AiWorkspaceEventPayload,
): void => {
  try {
    sink(name, payload);
  } catch {
    // Telemetry failures must never surface to the user.
  }
};
